import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function POHCalculator() {
  const [ohConcentration, setOhConcentration] = useState("");
  const [result, setResult] = useState<{ poh: number; ph: number } | null>(null);

  const calculate = () => {
    const oh = parseFloat(ohConcentration);

    if (isNaN(oh) || oh <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter a valid positive concentration.",
        variant: "destructive",
      });
      return;
    }

    const poh = -Math.log10(oh);
    const ph = 14 - poh;

    setResult({ poh, ph });

    toast({
      title: "Calculation Complete",
      description: `pOH: ${poh.toFixed(2)}, pH: ${ph.toFixed(2)}`,
    });
  };

  const reset = () => {
    setOhConcentration("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="pOH Calculator" 
      calculatorPath="/calculator/poh"
      category="chemistry"
      subcategory="Acids & Bases"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Calculate pOH and pH</CardTitle>
            <CardDescription>
              Calculate pOH and pH from hydroxide ion concentration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="oh">[OH⁻] Concentration (M)</Label>
                <Input
                  id="oh"
                  type="number"
                  step="any"
                  value={ohConcentration}
                  onChange={(e) => setOhConcentration(e.target.value)}
                  placeholder="Enter OH⁻ concentration"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <div className="space-y-4">
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">pOH</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.poh.toFixed(2)}</p>
                      <CopyResultButton value={result.poh.toFixed(2)} />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">pH</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.ph.toFixed(2)}</p>
                      <CopyResultButton value={result.ph.toFixed(2)} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formulas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="font-mono">pOH = -log₁₀[OH⁻]</p>
                <p className="font-mono">pH + pOH = 14</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
